import { Injectable } from "@angular/core";
import { HttpService } from "services/http.service";
import { ServiceBase } from "services/servicebase";
import { environment } from "../../../environments/environment";
import { PointTimePeriodModel } from "services/models/corporate/pointTimePeriod.model";
import { Observable } from "rxjs";
import { QueryParams } from "services/models/odata/queryParams";
import { map } from "rxjs/operators";
import { Response } from "../models/response";
import { Filter } from "services/models/odata/filter";
import moment from "moment-business-days";
import { ObjectMapper } from "json-object-mapper";

@Injectable()
export class PointTimePeriodService extends ServiceBase<PointTimePeriodModel> {
  constructor(http: HttpService) {
    super("pointtimeperiod", http, environment.apiUrl);
  }

  createEntity(input: any): PointTimePeriodModel {
    return PointTimePeriodModel.create(input);
  }

  changePeriodStatus(periodId: string, disableLoading: boolean = false) {
    return this.http
    .put(`${this.endpoint}/${periodId}/toggle-status`,"", disableLoading);
  }

  saveWithDisableLoading(
    id: number | string,
    model: PointTimePeriodModel,
    disableLoading: boolean
  ): Observable<Response<PointTimePeriodModel>> {
    const object = model;
    this.http.convertTimes(object);
    let serialized = ObjectMapper.serialize(object);
    serialized = serialized.length > 1 ? serialized : object.toJSON();

    const request$ = !id
      ? this.http.post(`/${this.endpoint}`, serialized, undefined, this.baseUrl, disableLoading)
      : this.http.put(this.endpoint, JSON.parse(serialized.toString()), disableLoading);

    return request$.pipe(
      map(() => {
        const response = new Response<PointTimePeriodModel>();
        return response;
      })
    );
  }

  checkPeriodAvailableByCompany(companyId: string, date: string): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.http.get(`${this.endpoint}/check-period-available-by-company/${companyId}/${date}`).subscribe(
        (res) => {
          observer.next(res.body);
          observer.complete();
        },
        (err) => observer.error(err)
      );
    });
  }

  listDistinctPeriods(): Observable<Response<PointTimePeriodModel[]>> {
    const params = {
      select: ['dateBegin', 'dateEnd'],
    } as QueryParams;
    const query = this.createBuildQuery(params);

    return this.http.get(`${this.endpoint}/odata${query}`, null, undefined, null, true)
      .pipe(
        map((res) => {
          const response = new Response<PointTimePeriodModel[]>();
          response.data = res.body.map((pp) => PointTimePeriodModel.create(pp));
          response.count = res.headers.get("x-count");

          return response;
        })
      );
  }

  getPeriodIdByCompanyAndDates(
    companyId: string,
    dateBegin: string,
    dateEnd: string,
    disableLoading: boolean = true
  ): Observable<string | null> {
    return this.getPeriodByCompanyAndDates(companyId, dateBegin, dateEnd, disableLoading).pipe(
      map((period) => period?.id ?? null)
    );
  }

  getPeriodByCompanyAndDates(
    companyId: string,
    dateBegin: string,
    dateEnd: string,
    disableLoading: boolean = true
  ): Observable<PointTimePeriodModel | null> {
    const beginStart = moment(dateBegin).startOf("day").toISOString();
    const beginEnd = moment(dateBegin).endOf("day").toISOString();
    const endStart = moment(dateEnd).startOf("day").toISOString();
    const endEnd = moment(dateEnd).endOf("day").toISOString();

    let filter = Filter.equalToGuid("companyId", companyId);
    filter = Filter.appendFilter(
      filter,
      "and",
      `dateBegin ge ${beginStart} and dateBegin le ${beginEnd}`
    );
    filter = Filter.appendFilter(
      filter,
      "and",
      `dateEnd ge ${endStart} and dateEnd le ${endEnd}`
    );

    const params = {
      select: ["id", "enable"],
      top: 1,
      filter,
    } as QueryParams;

    return this.list(params, disableLoading).pipe(
      map((res) => (res?.data?.length ? this.createEntity(res.data[0]) : null))
    );
  }
}